// [RD-PILL 2026-09-10] NEW FILE — Refunds and Disputes as ONE page; only the TABLE slides between them.
import React from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import RefundsPage from './RefundsPage';
import DisputesPage from './DisputesPage';
import { useAuth } from '../contexts/AuthContext';
import { viewFromPath } from '../components/SectionSwitcher';
import { easing, reducedMotion } from '../styles/motion';

/**
 * Rendered on BOTH /refunds and /disputes (App.tsx), and AppLayout gives those two routes one
 * PageFade key, so moving between them never remounts this.
 *
 * Both pages stay mounted, stacked in the same spot. Only each page's TABLE area (marked
 * data-rd-table) moves: picking Disputes slides the Refunds table out to the left while the
 * Disputes table comes in from the right; picking Refunds reverses it. The toolbar (marked
 * data-rd-toolbar) never moves — the two toolbars simply swap, which can't be seen because both
 * pills take their highlight from the route and move their thumbs in step (see SectionSwitcher).
 * Each page keeps its own month, team, Sales/PM, search and scroll position.
 */

/* EOMPage's EOM / Dashboard panel slide, so the app's two view toggles move the same way. The
   leaving page hides only once this has elapsed, so the delay below must stay equal to it. */
const SLIDE_S = '0.42s';

type Pos = 'center' | 'left' | 'right';

const offset = (pos: Pos) =>
  pos === 'center' ? 'none' : `translateX(${pos === 'left' ? -100 : 100}%)`;

/* Clip, not hidden — hidden would make this a scroll box the browser could shift sideways. */
const Stage = styled.div`
  position: relative;
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: clip;
  background: ${(p) => p.theme.surface};
`;

const Pane = styled.div<{ $pos: Pos }>`
  position: absolute;
  inset: 0;
  /* The page on show sits on top. The page leaving stays visible underneath until its table has
     slid out, then hides so nothing in it can be tabbed into or read out. */
  z-index: ${(p) => (p.$pos === 'center' ? 1 : 0)};
  visibility: ${(p) => (p.$pos === 'center' ? 'visible' : 'hidden')};
  transition: visibility 0s linear ${(p) => (p.$pos === 'center' ? '0s' : SLIDE_S)};

  /* The page root paints no background here (the Stage does), so the table leaving can be seen
     sliding out beneath the page arriving. */
  && > * { background: transparent; }

  /* The toolbar never moves — the leaving one hides at once, the arriving one shows at once. */
  & [data-rd-toolbar] {
    visibility: ${(p) => (p.$pos === 'center' ? 'visible' : 'hidden')};
  }

  /* Only the table travels. none (not translateX(0)) at rest, so it's no containing block for
     position:fixed content. */
  & [data-rd-table] {
    transform: ${(p) => offset(p.$pos)};
    transition: transform ${SLIDE_S} ${easing.emphasized};
  }

  ${reducedMotion} {
    &, & [data-rd-table] { transition: none; }
  }
`;

const RefundsDisputesPage: React.FC = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const allowed = new Set(user?.allowed_sections || []);
  const view = viewFromPath(pathname);
  // [FLUID-6 2026-10-12, user] "the first time when someone comes on the tab it shows the animation for the data, and
  // then in the toggle switch change it will not show an animation": each table's rows rise in when its data lands (the
  // shared DataTable — first visit, a month pick), and a switch between Refunds and Disputes only slides the table.

  // Granted only one of the two → that page alone, exactly as before (no slide, plain title).
  if (!isAdmin && !(allowed.has('refunds') && allowed.has('disputes'))) {
    return view === 'disputes' ? <DisputesPage /> : <RefundsPage />;
  }

  return (
    <Stage>
      <Pane $pos={view === 'refunds' ? 'center' : 'left'}>
        <RefundsPage />
      </Pane>
      <Pane $pos={view === 'disputes' ? 'center' : 'right'}>
        <DisputesPage />
      </Pane>
    </Stage>
  );
};

export default RefundsDisputesPage;
